import { ConversationMessage } from './types';

interface QueuedMessage {
  id: string;
  message: string;
  timestamp: Date;
  retryCount: number;
  maxRetries: number;
}

export class MessageQueue {
  private queue: QueuedMessage[] = [];
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 2000; // 2 seconds

  constructor() {
    this.loadQueue();
  }

  private loadQueue(): void {
    const stored = localStorage.getItem('agriagent_message_queue');
    if (stored) {
      const parsed = JSON.parse(stored);
      this.queue = parsed.map((q: any) => ({
        ...q,
        timestamp: new Date(q.timestamp),
      }));
    }
  }

  private saveQueue(): void {
    localStorage.setItem('agriagent_message_queue', JSON.stringify(this.queue));
  }

  addToQueue(message: string): string {
    const id = `queued_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const queuedMessage: QueuedMessage = {
      id,
      message,
      timestamp: new Date(),
      retryCount: 0,
      maxRetries: this.MAX_RETRIES,
    };

    this.queue.push(queuedMessage);
    this.saveQueue();
    return id;
  }

  removeFromQueue(id: string): void {
    this.queue = this.queue.filter((msg) => msg.id !== id);
    this.saveQueue();
  }

  getQueue(): QueuedMessage[] {
    return [...this.queue];
  }

  getPendingCount(): number {
    return this.queue.length;
  }

  async processQueue(
    sendFunction: (message: string) => Promise<void>,
    onProgress?: (processed: number, total: number) => void
  ): Promise<void> {
    const totalMessages = this.queue.length;
    let processed = 0;

    while (this.queue.length > 0) {
      const queuedMsg = this.queue[0];

      try {
        await sendFunction(queuedMsg.message);
        this.removeFromQueue(queuedMsg.id);
        processed++;
        onProgress?.(processed, totalMessages);
      } catch (error) {
        queuedMsg.retryCount++;

        if (queuedMsg.retryCount >= queuedMsg.maxRetries) {
          console.error('Max retries reached for message:', queuedMsg.id);
          this.removeFromQueue(queuedMsg.id);
          processed++;
          onProgress?.(processed, totalMessages);
        } else {
          // Move to end of queue for retry
          this.queue.shift();
          this.queue.push(queuedMsg);
          this.saveQueue();
          
          // Wait before next retry
          await new Promise((resolve) => setTimeout(resolve, this.RETRY_DELAY));
        }
      }
    }
  }

  clearQueue(): void {
    this.queue = [];
    this.saveQueue();
  }

  hasQueuedMessages(): boolean {
    return this.queue.length > 0;
  }
}

export const messageQueue = new MessageQueue();
